import { useState } from 'react';

import { convertToRaw, EditorState } from 'draft-js';

import { Button } from '@components/button';
import { createForm, useFormField } from '@components/form';
import { FormLabel } from '@components/form-label';
import { IconTemplate } from '@components/icons/template';
import { useSidebar, hideSidebar } from '@components/sidebar';
import { Sidebar } from '@components/sidebar';
import { Stack } from '@components/stack';
import { TextEditor } from '@components/text-editor';
import { placeholdersToBrackets } from '@components/text-editor/plugin-placeholder';
import { useScopedTranslate } from '@locale/translate';
import { draftToHtml } from '@utils/draftjs';

import { useCreateMessageTemplate } from 'api/companies/hooks';
import { usePlaceholderMap } from 'hooks/use-placeholder-map';

export const useMessageTemplateSidebar = () =>
  useSidebar(MessageTemplateSidebar, {
    variant: 'CONTENT_HEAVY',
  });

const MessageTemplateSidebar = () => {
  const { t } = useScopedTranslate(locale);
  const submit = useCreateMessageTemplate();
  const [isTitleEmpty, setIsTitleEmpty] = useState<boolean>(true);
  const [isTextEditorEmpty, setIsTextEditorEmpty] = useState<boolean>(true);
  const disableSave = isTitleEmpty || isTextEditorEmpty;
  return (
    <Sidebar>
      <Sidebar.SimpleHeader icon={IconTemplate} color="blue100" title={t('createMessage')} />
      <Sidebar.Content>
        <Form
          onSubmit={(data) => {
            submit.mutate({ templateType: 'message', ...data });
            hideSidebar();
          }}
        >
          <Stack space={6} orientation="vertical">
            <Form.TextField
              onChange={(title) => {
                setIsTitleEmpty(title.length === 0);
              }}
              placeholder={t('titlePlaceholder')}
              id="title"
              label={t('title')}
              validators={['required']}
            />
            <MessageEditor setEmpty={setIsTextEditorEmpty} />
          </Stack>
          <Form.Actions>
            <Button variant="secondary" onClick={hideSidebar} text={t('cancel')} />
            <Button
              isDisabled={disableSave}
              variant="primary-blue"
              type="submit"
              text={t('save')}
            />
          </Form.Actions>
        </Form>
      </Sidebar.Content>
    </Sidebar>
  );
};

const Form = createForm<MessageTemplateData>();

type MessageTemplateData = {
  title: string;
  contentHtml: string;
};

const MessageEditor = ({
  contentHtml,
  setEmpty,
}: {
  contentHtml?: string;
  setEmpty: (empty: boolean) => void;
}) => {
  const { t } = useScopedTranslate(messageEditor);
  const [editorState, setEditorState] = useState<EditorState>();

  const textProps = {
    save: t('save'),
    addLink: t('addLink'),
    linkToolTip: t('chooseText'),
    label: t('link'),
  };

  const emailProps = useFormField({
    id: 'contentHtml',
    label: t('label'),
    defaultValue: contentHtml,
    validators: ['required'],
  });
  const placeholderMap = usePlaceholderMap('message');

  const changeHtml = async () => {
    if (editorState) {
      const rawData = convertToRaw(editorState.getCurrentContent());
      const converted = placeholdersToBrackets(rawData);
      const html = draftToHtml(converted);
      emailProps.onChange(html);
    }
  };
  return (
    <Stack space={1}>
      <Stack space={1} orientation="horizontal" align="center">
        <FormLabel text={emailProps.label} />
      </Stack>
      <TextEditor
        onChange={(state) => {
          const empty = state.getCurrentContent().getPlainText().length === 0;
          setEmpty(empty);
          setEditorState(state);
        }}
        initialEditorState={contentHtml}
        onChangeHtml={changeHtml}
        variant="gray"
      >
        <Stack orientation="horizontal" justify="space-between" align="center">
          <TextEditor.Toolbar anchorButtonProps={textProps} />
          <TextEditor.PlaceholderMenu
            placeholderMap={placeholderMap}
            label={t('placeholderList')}
          />
        </Stack>
        <TextEditor.Input placeholder={t('placeholder')} />
      </TextEditor>
    </Stack>
  );
};

const messageEditor = {
  en: {
    placeholderList: 'Placeholder',
    placeholder: 'Compose a new message template',
    save: 'Save',
    addLink: 'Add link',
    chooseText: 'Select what you want to turn into a hyperlink',
    label: 'Content',
    link: 'Link',
  },
  is: {
    placeholderList: 'Staðsetningartexti',
    placeholder: 'Búðu til nýtt sniðmát',
    save: 'Vista',
    addLink: 'Hengja hlekk',
    chooseText: 'Veldu textann sem þú vilt breyta í hlekk',
    label: 'Skilaboð',
    link: 'Hlekkur',
  },
};

const locale = {
  en: {
    save: 'Save',
    createMessage: 'New email message template',
    back: 'Back',
    cancel: 'Cancel',
    title: 'Template title',
    titlePlaceholder: 'Add title',
  },
  is: {
    save: 'Vista',
    createMessage: 'Nýtt sniðmát fyrir skilaboð',
    back: 'Til baka',
    cancel: 'Hætta við',
    title: 'Titill sniðmáts',
    titlePlaceholder: 'Bæta við sniðmáti',
  },
};
